/**
 * Conditional Custom Fields API Utility
 * Provides 0/1 conditional logic for triggering custom fields APIs
 * 0 = Do NOT trigger the custom fields API
 * 1 = Trigger the custom fields API
 */

import axios from "axios";
import React from "react";
// Use BACKEND-compatible imports
const finalRouteApi = process.env.REACT_APP_Baseurl;
const getHeaders = () => {
  const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-access-token': token
  };
};

class ConditionalCustomFieldsApi {
  constructor() {
    this.enabled = null; // Cache the enabled state
    this.lastCheck = null; // Timestamp of last check
    this.cacheTimeout = 60000; // Cache for 1 minute
  }

  /**
   * Check if custom fields plugin is enabled
   * Uses caching to avoid repeated API calls
   * @returns {Promise<boolean>} true if enabled (1), false if disabled (0)
   */
  async isCustomFieldsEnabled() {
    const now = Date.now();
    
    // Return cached result if still valid
    if (this.enabled !== null && this.lastCheck && (now - this.lastCheck < this.cacheTimeout)) {
      return this.enabled;
    }

    try {
      // Try the new dashboard endpoint first
      const response = await axios.get(
        `${finalRouteApi}/custom-fields/dashboard`,
        { headers: getHeaders() }
      );
      
      this.enabled = response.data?.enabled === true;
      this.lastCheck = now;
      
      console.log('[CustomFields] Plugin status check:', this.enabled ? 'ENABLED (1)' : 'DISABLED (0)');
      return this.enabled;
      
    } catch (error) {
      try {
        // Fallback to legacy endpoint
        const fallbackResponse = await axios.get(
          `${finalRouteApi}/admin/features/custom-fields`,
          { headers: getHeaders() }
        );
        
        this.enabled = fallbackResponse.data?.enabled === true;
        this.lastCheck = now;
        
        console.log('[CustomFields] Plugin status check (fallback):', this.enabled ? 'ENABLED (1)' : 'DISABLED (0)');
        return this.enabled;
        
      } catch (fallbackError) {
        console.warn('[CustomFields] Could not determine plugin status, defaulting to DISABLED (0)');
        this.enabled = false;
        this.lastCheck = now;
        return false;
      }
    }
  }

  /**
   * Conditionally execute custom fields API call
   * Only executes if custom fields plugin is enabled (1)
   * @param {Function} apiCall - The async function that makes the custom fields API call
   * @param {*} fallbackValue - Value to return if plugin is disabled (default: null)
   * @returns {Promise<*>} API result if enabled, fallbackValue if disabled
   */
  async executeIfEnabled(apiCall, fallbackValue = null) {
    const enabled = await this.isCustomFieldsEnabled();
    
    if (!enabled) {
      console.log('[CustomFields] API call skipped - plugin is DISABLED (0)');
      return fallbackValue;
    }
    
    console.log('[CustomFields] Executing API call - plugin is ENABLED (1)');
    try {
      return await apiCall();
    } catch (error) {
      console.error('[CustomFields] API call failed:', error);
      throw error;
    }
  }

  /**
   * Conditionally save custom field values
   * @param {string} baseURL - Base URL for API
   * @param {Object} headers - Request headers
   * @param {string} entityType - Entity type (e.g., 'plan', 'faq')
   * @param {string|number} entityId - Entity ID
   * @param {Object} values - Custom field values
   * @returns {Promise<Object|null>} API response or null if disabled
   */
  async saveCustomFieldValues({ baseURL, headers, entityType, entityId, values }) {
    return this.executeIfEnabled(async () => {
      if (!baseURL) throw new Error("Missing baseURL for saving additional fields");
      if (!entityType) throw new Error("Missing entity type for saving additional fields");
      if (entityId === undefined || entityId === null || entityId === "") {
        throw new Error("Missing entity id for saving additional fields");
      }

      const res = await axios.post(
        `${baseURL}/admin/custom-field-values`,
        { entity_type: entityType, entity_id: entityId, values },
        { headers }
      );
      
      const data = res?.data;
      if (!data?.status) {
        throw new Error(data?.message || "Failed to save additional fields");
      }
      return data;
    });
  }

  /**
   * Conditionally fetch custom field values
   * @param {string} entityType - Entity type
   * @param {string|number} entityId - Entity ID
   * @returns {Promise<Object|null>} Field values or null if disabled
   */
  async getCustomFieldValues(entityType, entityId) {
    return this.executeIfEnabled(async () => {
      const response = await axios.get(
        `${finalRouteApi}/custom-field-values`,
        {
          params: { entity_type: entityType, entity_id: entityId },
          headers: getHeaders()
        }
      );
      return response.data?.values || {};
    }, {});
  }

  /**
   * Conditionally fetch custom fields for a form
   * @param {string} pageIdentifier - Page identifier (e.g., 'videos.create')
   * @returns {Promise<Array|null>} Fields array or empty array if disabled
   */
  async getCustomFieldsForForm(pageIdentifier) {
    return this.executeIfEnabled(async () => {
      const response = await axios.get(
        `${finalRouteApi}/custom-fields-for-form`,
        {
          params: { pageIdentifier },
          headers: getHeaders()
        }
      );
      return response.data?.fields || [];
    }, []);
  }

  /**
   * Force refresh the enabled status cache
   * Useful when you know the plugin status has changed
   */
  refreshCache() {
    this.enabled = null;
    this.lastCheck = null;
    console.log('[CustomFields] Status cache refreshed');
  }

  /**
   * Manually set the enabled status (for testing or when known)
   * @param {boolean} enabled - Whether custom fields is enabled
   */
  setEnabledStatus(enabled) {
    this.enabled = Boolean(enabled);
    this.lastCheck = Date.now();
    console.log('[CustomFields] Status manually set to:', this.enabled ? 'ENABLED (1)' : 'DISABLED (0)');
  }

  /**
   * Get a conditional component wrapper
   * Returns the component if enabled, null if disabled
   * @param {React.Component} component - Component to conditionally render
   * @param {React.Component} fallback - Fallback component if disabled (optional)
   */
  async getConditionalComponent(component, fallback = null) {
    const enabled = await this.isCustomFieldsEnabled();
    return enabled ? component : fallback;
  }
}

// Export singleton instance
const conditionalCustomFieldsApi = new ConditionalCustomFieldsApi();

export default conditionalCustomFieldsApi;

// Named exports for convenience
export const {
  isCustomFieldsEnabled,
  executeIfEnabled,
  saveCustomFieldValues,
  getCustomFieldValues,
  getCustomFieldsForForm,
  refreshCache,
  setEnabledStatus,
  getConditionalComponent
} = conditionalCustomFieldsApi;

/**
 * HOC for conditionally rendering components based on custom fields status
 * @param {React.Component} WrappedComponent - Component to conditionally render
 * @param {React.Component} FallbackComponent - Component to show if disabled
 */
export const withCustomFieldsCondition = (WrappedComponent, FallbackComponent = null) => {
  return function ConditionalCustomFieldsComponent(props) {
    const [enabled, setEnabled] = React.useState(null);
    
    React.useEffect(() => {
      conditionalCustomFieldsApi.isCustomFieldsEnabled().then(setEnabled);
    }, []);
    
    if (enabled === null) {
      return <div>Loading...</div>; // Or your loading component
    }
    
    if (!enabled) {
      return FallbackComponent ? <FallbackComponent {...props} /> : null;
    }
    
    return <WrappedComponent {...props} />;
  };
};